import React, { useState, useEffect } from 'react';
import { Link, Redirect } from 'react-router-dom';
import userStore from '../../../Stores/User';
import { observer } from 'mobx-react';
import Play from '../../DiscoverNew/Icons/Play.svg';
import Pause from '../../DiscoverNew/Icons/Pause.svg';
import { loadStripe } from '@stripe/stripe-js';
import mixpanel from 'mixpanel-browser';
import loadingIcon from '../../../Assets/loading.gif';
import { createSubscriptionCheckoutSessionWithTrial, validateCoupon, cancelSetupIntent } from '../../../audiblogsApi';
import queryString from 'query-string';
import CheckoutForm from '../../CheckoutForm';

const STRIPE_API_KEY = process.env.REACT_APP_STRIPE_API_KEY;
const stripePromise = loadStripe(STRIPE_API_KEY);

const InitialCheckoutForm = observer(({ onCouponSubmit, couponError, couponSuccess, onAddPaymentDetails, couponCode, setCouponCode, isLoading }) => {
  return (
    <div>
      {/* Coupon Input */}
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '15px'
      }}>
        <input
          type="text"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value)}
          placeholder="Coupon code"
          style={{
            flex: 1,
            padding: '8px 12px',
            backgroundColor: 'rgba(24,15,34,1)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        <button
          onClick={() => onCouponSubmit(couponCode)}
          disabled={isLoading}
          style={{
            padding: '8px 12px',
            backgroundColor: 'transparent',
            color: '#fff',
            border: '1px solid #fff',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            opacity: isLoading ? 0.7 : 1
          }}
        >
          {isLoading ? 'Applying...' : 'Apply'}
        </button>
      </div>

      {/* Coupon Messages */}
      {couponError && (
        <div style={{ 
          color: '#df1b41', 
          fontSize: '14px',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '5px'
        }}>
          <span style={{ fontWeight: 'bold' }}>✕</span> {couponError}
        </div>
      )}
      
      {couponSuccess && (
        <div style={{ 
          color: '#4ade80', 
          fontSize: '14px',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '5px'
        }}>
          <span style={{ fontWeight: 'bold' }}>✓</span> {couponSuccess}
        </div>
      )}

      {/* Add Payment Details Button */}
      <div 
        onClick={onAddPaymentDetails}
        role="button"
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: '#3b82f6 !important',
          background: '#3b82f6 !important',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '16px',
          textAlign: 'center',
          userSelect: 'none'
        }}
      >
        Add Payment Details
      </div>

      {/* Terms - Only show in InitialCheckoutForm */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '20px',
        fontSize: '12px',
        color: '#666'
      }}>
        By continuing, you agree to our{' '}
        <a 
          href="/terms" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#fff', textDecoration: 'underline' }}
        >
          Terms of Service
        </a>
      </div>
    </div>
  );
});

const ChooseVoice = observer((props) => {
  const [quality, setQuality] = useState('Unreal');
  const [subscriptionType, setSubscriptionType] = useState('basic-monthly');
  const [sessionId, setSessionId] = useState(null);
  const params = queryString.parse(props.location.search);
  const app = params.app || '';
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showPaymentElement, setShowPaymentElement] = useState(false);
  const [paymentKey, setPaymentKey] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [setupIntentId, setSetupIntentId] = useState(null);

  useEffect(() => {
    if (!userStore.user) {
      setShouldRedirect(true);
      return;
    }
    if (userStore.user && userStore.user.vip_id) {
      setShouldRedirect(true);
    }
  }, [userStore.user]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (showPaymentElement) {
      setShowPaymentElement(true);
    }
  }, [showPaymentElement]);

  const handleCouponSubmit = async (code) => {
    if (!code.trim()) return;
    setIsLoading(true);
    try {
      const result = await validateCoupon(userStore.idToken, code.trim());
      if (result.error) {
        setCouponError(result.error);
      } else if (result.valid && result.coupon_details) {
        setCouponSuccess(formatCouponMessage(result.coupon_details));
        setCouponCode(code);
      } else {
        setCouponError('Invalid coupon code');
      }
    } catch (error) {
      setCouponError('Failed to validate coupon');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCouponMessage = (couponDetails) => {
    let message = '';
    if (couponDetails.percent_off) {
      message = `${couponDetails.percent_off}% off`;
    } else if (couponDetails.amount_off) {
      const amount = (couponDetails.amount_off / 100).toFixed(2);
      const currency = couponDetails.currency.toUpperCase();
      message = `${currency} ${amount} off`;
    }

    if (couponDetails.duration === 'repeating' && couponDetails.duration_in_months) {
      message += ` for ${couponDetails.duration_in_months} months`;
    } else if (couponDetails.duration === 'once') {
      message += ` (one-time)`;
    } else if (couponDetails.duration === 'forever') {
      message += ` forever`;
    }

    if (couponDetails.restrictions?.minimum_amount) {
      const minAmount = (couponDetails.restrictions.minimum_amount / 100).toFixed(2);
      const currency = couponDetails.restrictions.minimum_amount_currency.toUpperCase();
      message += ` (min. ${currency} ${minAmount})`;
    }

    return message || 'Coupon applied successfully!';
  };

  const handlePaymentDetails = () => {
    setPaymentKey(prev => prev + 1);
    setShowPaymentElement(true);
  };

  const handleChangePlan = async () => {
    if (setupIntentId) {
      setIsLoading(true);
      try {
        await cancelSetupIntent(userStore.idToken, setupIntentId);
        setSetupIntentId(null);
        setShowPaymentElement(false);
        localStorage.removeItem('lastSetupIntentId');
        setPaymentKey(prev => prev + 1);
      } catch (error) {
        console.error('Error canceling setup intent:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubscriptionTypeChange = (newType) => {
    if (!showPaymentElement) {
      setSubscriptionType(newType);
    }
  };

  if (shouldRedirect) {
    return <Redirect to={userStore.user ? `quickstart?app=${app}` : `/signin?redirected_from=signup&app=${app}`} />;
  }

  const firstName = userStore.user?.displayName?.split(' ')[0];
  const welcomeMessage = firstName ? `👋 Hi ${firstName},\ntry free for 3 days` : 'Start your free trial';

  return (
    <div className="center-static" style={{ 
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100%',
      padding: '20px',
      boxSizing: 'border-box',
      overflow: 'auto'
    }}>
      <h1 className="headline" style={{ marginBottom: '0px', whiteSpace: 'pre-line' }}>
        {welcomeMessage}
      </h1>
      <h2 className="subheadline" style={{ whiteSpace: 'pre-line' }}>Choose your plan</h2>
      
      {/* Subscription Type Selection */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: '10px', 
        margin: '20px 0',
        opacity: showPaymentElement ? 0.5 : 1,
        pointerEvents: showPaymentElement ? 'none' : 'auto',
        flexWrap: 'wrap'
      }}>
        <div 
          onClick={() => handleSubscriptionTypeChange(subscriptionType.includes('pro') ? 'pro-monthly' : 'basic-monthly')}
          style={{
            padding: '8px 20px',
            border: '2px solid hsla(0,0%,100%,.1)',
            borderRadius: '20px',
            cursor: showPaymentElement ? 'not-allowed' : 'pointer',
            backgroundColor: subscriptionType.includes('monthly') ? 'rgba(255,255,255,0.1)' : 'transparent',
          }}
        >
          Monthly
        </div>

        <div
          onClick={() => handleSubscriptionTypeChange(subscriptionType.includes('pro') ? 'pro-yearly' : 'basic-yearly')} 
          style={{
            padding: '8px 20px',
            border: '2px solid hsla(0,0%,100%,.1)',
            borderRadius: '20px',
            cursor: showPaymentElement ? 'not-allowed' : 'pointer',
            backgroundColor: subscriptionType.includes('yearly') ? 'rgba(255,255,255,0.1)' : 'transparent',
          }}
        >
          Yearly <span style={{ color: '#4CAF50' }}>(Save 17%)</span>
        </div>
      </div>

      {/* Plan Selection Cards */}
      <div 
        style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'center', 
          alignItems: 'center',
          gap: '20px', 
          margin: '20px 0',
          width: '100%',
          maxWidth: '100%',
          marginLeft: 'auto',
          marginRight: 'auto',
          opacity: showPaymentElement ? 0.5 : 1,
          pointerEvents: showPaymentElement ? 'none' : 'auto',
          flexWrap: 'wrap',
          boxSizing: 'border-box',
          padding: '0 20px'
        }}
      >
        {/* Basic Plan Card */}
        <div
          onClick={() => handleSubscriptionTypeChange(subscriptionType.includes('yearly') ? 'basic-yearly' : 'basic-monthly')}
          style={{
            padding: '20px',
            border: '2px solid hsla(0,0%,100%,.1)',
            borderRadius: '8px',
            width: isMobile ? '100%' : 'calc(50% - 10px)',
            maxWidth: '300px',
            cursor: showPaymentElement ? 'not-allowed' : 'pointer',
            backgroundColor: subscriptionType.includes('basic') ? 'rgba(255,255,255,0.1)' : 'transparent',
            boxSizing: 'border-box'
          }}
        >
          <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>Unlimited Basic</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'rgb(255 143 65)' }}>
            {subscriptionType.includes('yearly') ? '$49.99' : '$4.99'}
          </div>
          <div style={{ opacity: 0.8 }}>per {subscriptionType.includes('yearly') ? 'year' : 'month'}</div>
          <ul style={{ textAlign: 'left', marginTop: '15px', paddingLeft: '20px' }}>
            <li>Unlimited articles</li>
            <li>Life-like voices</li>
            <li>Listen on your podcast player</li>
            <li>Automated content creation</li>
            <li>Up to 3 Feeds</li>
            <li>Add urls, pdfs, and more</li>
          </ul>
        </div>

        {/* Pro Plan Card */}
        <div
          onClick={() => handleSubscriptionTypeChange(subscriptionType.includes('yearly') ? 'pro-yearly' : 'pro-monthly')}
          style={{
            padding: '20px',
            border: '2px solid hsla(0,0%,100%,.1)',
            borderRadius: '8px',
            width: isMobile ? '100%' : 'calc(50% - 10px)',
            maxWidth: '300px',
            cursor: showPaymentElement ? 'not-allowed' : 'pointer',
            backgroundColor: subscriptionType.includes('pro') ? 'rgba(255,255,255,0.1)' : 'transparent',
            boxSizing: 'border-box'
          }}
        >
          <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>Unlimited Pro</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'rgb(255 143 65)' }}>
            {subscriptionType.includes('yearly') ? '$99.99' : '$9.99'}
          </div>
          <div style={{ opacity: 0.8 }}>per {subscriptionType.includes('yearly') ? 'year' : 'month'}</div>
          <ul style={{ textAlign: 'left', marginTop: '15px', paddingLeft: '20px' }}>
            <li>Everything in Basic</li>
            <li>Premium voices</li>
            <li>Multi-language support</li>
            <li>AI Summaries</li>
            <li>Unlimited Feeds</li>
            <li>More advanced features</li>
          </ul>
        </div>
      </div>

      {/* Change Plan Button */}
      {showPaymentElement && (
        <button 
          onClick={handleChangePlan}
          disabled={isLoading}
          style={{
            margin: '20px auto',
            padding: '8px 20px',
            backgroundColor: 'transparent',
            border: '1px solid #fff',
            borderRadius: '4px',
            color: '#fff',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            width: 'fit-content',
            fontSize: '14px',
            opacity: isLoading ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {isLoading ? (
            <>
              <img src={loadingIcon} alt="Loading..." style={{ width: '16px', height: '16px' }} />
              Changing Plan...
            </>
          ) : (
            'Change Plan'
          )}
        </button>
      )}

      {/* Coupon and Payment Section */}
      <div style={{ 
        maxWidth: '100%', 
        margin: '20px auto',
        padding: '20px',
        backgroundColor: 'rgba(24,15,34,0.5)',
        borderRadius: '8px',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <CheckoutForm 
          planType={subscriptionType} 
          key={paymentKey}
          couponCode={couponCode}
          onSetupIntentCreated={setSetupIntentId}
          onShowPaymentElement={setShowPaymentElement}
        />
      </div>
    </div>
  );
});

export default ChooseVoice;
